/**
 * SABA: Shopify Forms Embed Style Overrides
 *
 * The Shopify Forms app renders inside a Shadow DOM (<shopify-forms-embed>)
 * so regular CSS cannot reach its elements. This script waits for the
 * shadow root to appear and injects a <style> tag with SABA brand overrides.
 */

(function () {
  'use strict';

  var SABA_STYLES = `
    /* ── SABA Typography ────────────────────────── */

    * {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
    }

    /* ── Heading ────────────────────────────────── */

    [class*="_textHeading"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-h2) !important;
      font-weight: var(--saba-font-weight-bold) !important;
      text-transform: uppercase !important;
      letter-spacing: 0 !important;
      line-height: normal !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      margin: 0 0 var(--saba-space-16) 0 !important;
    }

    /* ── Body text ──────────────────────────────── */

    [class*="_textBody"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-body-xs) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      line-height: 1.4 !important;
      color: rgb(var(--saba-color-brand-black)) !important;
    }

    [class*="_textBody"] p {
      margin: 0 !important;
      font-family: inherit !important;
      font-size: inherit !important;
      line-height: inherit !important;
      color: inherit !important;
    }

    /* ── Form header (heading + description) ────── */

    [class*="_formHeader"] {
      margin: 0 0 var(--saba-space-32) 0 !important;
    }

    [class*="_formHeader"] [class*="_textHeading"] {
      margin: 0 0 var(--saba-space-16) 0 !important;
    }

    /* ── Input fields ───────────────────────────── */

    [class*="_formInputField"]:not(label) {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-body-xs) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      letter-spacing: 0 !important;
      text-transform: uppercase !important;
      background-color: transparent !important;
      border: 1px solid rgb(var(--saba-color-neutral-light-grey)) !important;
      border-radius: 0 !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      box-shadow: none !important;
      transition: border-color 0.2s ease !important;
    }

    [class*="_formInputField"]:not(label):focus {
      border-color: rgb(var(--saba-color-brand-black)) !important;
      outline: none !important;
      box-shadow: none !important;
    }

    /* ── Input labels ───────────────────────────── */

    [class*="_formInputFieldLabel"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      font-size: 10px !important;
      letter-spacing: 0 !important;
      text-transform: uppercase !important;
      color: rgb(var(--saba-color-neutral-tertiary-grey)) !important;
    }

    /* ── Phone input ────────────────────────────── */

    [class*="_formPhoneInputField"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-body-xs) !important;
      background-color: transparent !important;
      border: 1px solid rgb(var(--saba-color-neutral-light-grey)) !important;
      border-radius: 0 !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      box-shadow: none !important;
    }

    [class*="_formPhoneInputField"]:focus {
      border-color: rgb(var(--saba-color-brand-black)) !important;
      outline: none !important;
      box-shadow: none !important;
    }

    [class*="_formPhoneInputContainer"] {
      border-radius: 0 !important;
      box-shadow: none !important;
      border: none !important;
    }

    [class*="_selectToggle"] {
      min-height: 45.5px !important;
      border-radius: 0 !important;
      border: 1px solid rgb(var(--saba-color-neutral-light-grey)) !important;
      background-color: transparent !important;
      box-shadow: none !important;
    }

    /* Open (dropdown rendered) or focused select gets the brand-black border,
       matching the focus treatment used by text inputs. */
    [class*="_selectContainer"]:has([class*="_dropdownContainer"]) [class*="_selectToggle"],
    [class*="_selectToggle"]:focus {
      border-color: rgb(var(--saba-color-brand-black)) !important;
      outline: none !important;
      box-shadow: none !important;
    }

    [class*="_selectToggleText"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-body-xs) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      letter-spacing: 0 !important;
      text-transform: uppercase !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      border: none !important;
    }

    /* Reposition the external "Month" label so it sits inside the toggle box as a
       floating label, matching the inline label pattern used by the text inputs.
       The Month _selectContainer already has inline position:relative, so the
       absolute positioning anchors to it. The phone country _selectContainer has no
       _label child, so it is untouched. */
    [class*="_selectContainer"] > [class*="_label"] {
      position: absolute !important;
      top: 16px !important;
      left: 16px !important;
      margin: 0 !important;
      padding: 0 !important;
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      font-size: 10px !important;
      letter-spacing: 0 !important;
      text-transform: uppercase !important;
      color: rgb(var(--saba-color-neutral-tertiary-grey)) !important;
      pointer-events: none !important;
      z-index: 1 !important;
    }

    /* Hide the floating label once a value is selected. The Forms app does not toggle
       a class for the filled state; the only diff between empty and filled is the
       text inside _selectToggleText, so we gate via :has + :not(:empty). */
    [class*="_selectContainer"]:has([class*="_selectToggleText"]:not(:empty)) > [class*="_label"] {
      display: none !important;
    }

    [class*="_arrowDownIcon"] {
      display: none !important;
    }

    /* ── Checkboxes ─────────────────────────────── */

    [class*="_checkboxInput"] {
      border-radius: 0 !important;
      border: 1px solid rgb(var(--saba-color-neutral-secondary-grey)) !important;
      accent-color: rgb(var(--saba-color-brand-black)) !important;
    }

    [class*="_checkboxField"] {
      align-items: start !important;
    }

    [class*="_checkboxField"] span[class*="_textBody"] {
      font-size: var(--saba-text-body-xs) !important;
      line-height: 1.5 !important;
    }

    /* ── Submit button ──────────────────────────── */

    [class*="_formSubmitButton"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-body-xs) !important;
      font-weight: var(--saba-font-weight-bold) !important;
      text-transform: uppercase !important;
      letter-spacing: 0.06em !important;
      background-color: rgb(var(--saba-color-brand-black)) !important;
      color: rgb(var(--saba-color-brand-bone-white)) !important;
      border: none !important;
      border-radius: 0 !important;
      padding: 16px 32px !important;
      cursor: pointer !important;
      transition: opacity 0.2s ease !important;
    }

    [class*="_formSubmitButton"]:hover {
      opacity: 0.8 !important;
    }

    /* ── Disclaimer / footer text ───────────────── */

    [class*="_formDisclaimer"] {
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-body-xs) !important;
      color: rgb(var(--saba-color-neutral-tertiary-grey)) !important;
    }

    /* ── Form field containers ──────────────────── */

    [class*="_formFieldContainer"] {
      margin-bottom: 0 !important;
    }

    /* "Date of Birth" group heading injected above the birthday-day field. The
       pseudo is absolutely positioned above the container box because the inner
       floating label is already absolutely positioned at top:17px and would
       collide with an in-flow pseudo. The container gets margin-top to make
       room for the floating heading. */
    [class*="_formFieldContainer"]:has([id="label-custom#birthday_day"]) {
      margin-top: 24px !important;
      position: relative !important;
    }

    [class*="_formFieldContainer"]:has([id="label-custom#birthday_day"])::before {
      content: "Date of Birth" !important;
      position: absolute !important;
      bottom: calc(100% + 10px) !important;
      left: 0 !important;
      font-family: var(--saba-font-family, var(--text-font-family)) !important;
      font-size: var(--saba-text-h2) !important;
      font-weight: var(--saba-font-weight-bold) !important;
      text-transform: uppercase !important;
      letter-spacing: 0 !important;
      line-height: normal !important;
      color: rgb(var(--saba-color-brand-black)) !important;
    }

    /* ── Consent checkbox containers ─────────────── */

    [class*="_consentCheckboxContainer"] {
      margin: 0 !important;
      padding: 0 !important;
    }
  `;

  /* ------------------------------------------------------------------
   * Collection Light-look variant - bone-white text on input fields
   * and submit button. Toggled by `saba-collection-look-light` on <html>,
   * set by snippets/saba-collection-look.liquid when the collection's
   * Look metaobject has text_theme == "Light".
   * ------------------------------------------------------------------ */
  var LIGHT_STYLES = `
    [class*="_formInputField"]:not(label) {
      color: rgb(var(--saba-color-brand-bone-white)) !important;
    }

    [class*="_formInputFieldLabel"] {
      color: rgb(var(--saba-color-brand-bone-white)) !important;
    }

    [class*="_formSubmitButton"] {
      color: rgb(var(--saba-color-brand-bone-white)) !important;
    }
  `;

  /* ------------------------------------------------------------------
   * Footer variant - compact single-row layout to match
   * .saba-footer__form-row (email input + submit on a bottom border).
   * Applied only to embeds inside `.saba-footer__apps`.
   * ------------------------------------------------------------------ */
  var FOOTER_STYLES = `
    /* Form container: flush, no margin, no min-height */
    [class*="_formContainer"] {
      margin: 0 !important;
    }

    /* No measure of its own: the wrapper (.saba-footer__apps) carries the
       newsletter's 400px column, so an imposed max-width here fought it. */
    [class*="_inline"] [class*="_formContainer"] {
      min-height: 0 !important;
      max-width: none !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    /* Grid cells: flush, no padding */
    [class*="_gridItem"],
    [class*="_gridItemContent"] {
      padding: 0 !important;
    }

    /* Form root: flex row with bottom border */
    form {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: var(--saba-space-16) !important;
      border-bottom: 1px solid rgb(var(--saba-color-neutral-tertiary-grey)) !important;
      padding: 0 0 9px 0 !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    /* Field container takes remaining width, no margin, relative for label overlay */
    [class*="_formFieldContainer"] {
      position: relative !important;
      flex: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
      min-width: 0 !important;
      width: auto !important;
    }

    /* Label acts as a placeholder overlay - sits on top of the input */
    [class*="_formInputFieldLabel"] {
      position: absolute !important;
      inset: 0 !important;
      display: flex !important;
      align-items: center !important;
      margin: 0 !important;
      padding: 0 !important;
      pointer-events: none !important;
      transform: none !important;
      font-family: var(--saba-font-family) !important;
      font-size: var(--saba-text-body-sm) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      text-transform: none !important;
      letter-spacing: 0 !important;
      color: rgb(var(--text-color)) !important;
      opacity: 1 !important;
      transition: opacity 0.15s ease !important;
    }

    /* Hide label once the input receives focus */
    [class*="_formFieldContainer"]:focus-within [class*="_formInputFieldLabel"] {
      opacity: 0 !important;
    }

    /* Keep label hidden once the input has content (Shopify Forms adds
       _inputFilled to the label element when the field is non-empty).
       Also neutralise the native float-label transform. */
    [class*="_formFieldContainer"] [class*="_inputFilled"] {
      opacity: 0 !important;
      transform: none !important;
    }

    /* Hide first inner div of the fieldset (header/label row) */
    [class*="_formFieldset"] > div:first-child {
      display: none !important;
    }

    /* Input: borderless, transparent, flush-left */
    [class*="_formInputField"]:not(label) {
      border: none !important;
      background: transparent !important;
      padding: 0 !important;
      width: 100% !important;
      min-height: 0 !important;
      box-shadow: none !important;
    }

    [class*="_formInputField"]:not(label):focus {
      border: none !important;
      box-shadow: none !important;
    }

    /* Submit button: plain text, sits inline on the right */
    [class*="_formSubmitButton"] {
      background: transparent !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      padding: 0 !important;
      width: auto !important;
      flex: 0 0 auto !important;
      margin: 0 !important;
      letter-spacing: 0.06em !important;
      box-shadow: none !important;
    }

    [class*="_formSubmitButton"]:hover {
      background: transparent !important;
      opacity: 0.7 !important;
    }

    /* ── Success / error banner ────────────────── */

    [class*="_formBannerContainer"] {
      margin: 0 !important;
    }

    [class*="_formBannerHeading"] {
      font-family: var(--saba-font-family) !important;
      font-size: var(--saba-text-h2) !important;
      font-weight: var(--saba-font-weight-bold) !important;
      text-transform: uppercase !important;
      letter-spacing: 0 !important;
      line-height: normal !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      margin: 0 0 var(--saba-space-16) 0 !important;
    }

    [class*="_formBannerContainer"] p {
      font-family: var(--saba-font-family) !important;
      font-size: var(--saba-text-body-xs) !important;
      font-weight: var(--saba-font-weight-regular) !important;
      line-height: 1.4 !important;
      color: rgb(var(--saba-color-brand-black)) !important;
      margin: 0 !important;
    }
  `;

  function injectStyles(shadowRoot, cssText, marker, position) {
    if (shadowRoot.querySelector('[' + marker + ']')) return;
    var style = document.createElement('style');
    style.setAttribute(marker, '');
    style.textContent = cssText;
    if (position === 'append') {
      shadowRoot.appendChild(style);
    } else {
      shadowRoot.prepend(style);
    }
  }

  function isFooterEmbed(embed) {
    return !!(embed.closest && embed.closest('.saba-footer__apps'));
  }

  /* ------------------------------------------------------------------
   * Suppress the auto-scroll Shopify Forms triggers on submit (it
   * scrollIntoView()s / focuses the success banner). We capture the
   * scroll position on submit and restore it once the banner is
   * inserted into the shadow DOM. Also patch the banner's scroll &
   * focus methods so later calls can't move the viewport.
   * ------------------------------------------------------------------ */
  function trapScroll(embed) {
    var shadowRoot = embed.shadowRoot;
    if (!shadowRoot || shadowRoot._sabaScrollTrapped) return;
    shadowRoot._sabaScrollTrapped = true;

    var savedX = null;
    var savedY = null;

    shadowRoot.addEventListener('submit', function () {
      savedX = window.scrollX;
      savedY = window.scrollY;
    }, true);

    var bannerSelector = '[class*="_formBannerContainer"]';

    function neutralise(el) {
      if (!el || el._sabaScrollPatched) return;
      el._sabaScrollPatched = true;
      el.scrollIntoView = function () {};
      var originalFocus = el.focus && el.focus.bind(el);
      if (originalFocus) {
        el.focus = function (opts) {
          originalFocus(Object.assign({}, opts || {}, { preventScroll: true }));
        };
      }
    }

    function restoreIfNeeded() {
      if (savedY === null) return;
      var x = savedX, y = savedY;
      savedX = null;
      savedY = null;
      requestAnimationFrame(function () { window.scrollTo(x, y); });
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(bannerSelector)) {
            neutralise(node);
            restoreIfNeeded();
          } else if (node.querySelectorAll) {
            var nested = node.querySelectorAll(bannerSelector);
            if (nested.length) {
              for (var k = 0; k < nested.length; k++) neutralise(nested[k]);
              restoreIfNeeded();
            }
          }
        }
      }
    });
    observer.observe(shadowRoot, { childList: true, subtree: true });
  }

  function processEmbeds() {
    var embeds = document.querySelectorAll('shopify-forms-embed');
    var isCollectionLookLight = document.documentElement.classList.contains('saba-collection-look-light');
    embeds.forEach(function (embed) {
      if (!embed.shadowRoot) return;
      injectStyles(embed.shadowRoot, SABA_STYLES, 'data-saba-forms-override');
      if (isFooterEmbed(embed)) {
        injectStyles(embed.shadowRoot, FOOTER_STYLES, 'data-saba-forms-footer', 'append');
        trapScroll(embed);
      }
      if (isCollectionLookLight) {
        injectStyles(embed.shadowRoot, LIGHT_STYLES, 'data-saba-forms-light', 'append');
      }
    });
  }

  /* Process any embeds already present, then keep observing for new ones.
     All inject/trap helpers are idempotent (marker-guarded), so re-running
     on every mutation is cheap and ensures forms that mount late (e.g.
     multiple embeds hydrating asynchronously) still get styled. */
  processEmbeds();
  var observer = new MutationObserver(processEmbeds);
  observer.observe(document.body, { childList: true, subtree: true });

  /* Safety timeout - stop observing after 15s (forms should be mounted by then) */
  setTimeout(function () { observer.disconnect(); }, 15000);
})();
